import {
  AppShell,
  Button,
  Checkbox,
  Flex,
  Group,
  Loader,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import {
  EmailGuestsRequest,
  useGetEmailTemplatesQuery,
  useEmailGuestsMutation,
} from "../slices/api/emailApi";
import { JSX, useEffect, useState } from "react";
import dayjs from "dayjs";
import { useLocation } from "wouter";
import { TextSearchBox } from "../shared/components/TextSearchBox";
import { IconEmailTemplate } from "../shared/components/Icons";
import { useGetVentQuery } from "../slices/api/ventsApi";
import { RootState } from "../store";
import { useAppDispatch, useAppSelector } from "../hooks";
import { initialize, updateField } from "../slices/form/emailGuests";
import {
  displayErrorMessage,
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { isValidEmail } from "../shared/utility";
import { EbTooltip } from "../shared/components/EbTooltip";
import { EbActionIcon } from "../shared/components/EbActionItem";
import { LinkAnchorToVentEmailTemplates } from "../shared/components/LinkAnchors";

interface Props {
  // Guest IDs selected to receive email.
  guestIds: string[];
  ventId: string;
  onClose: () => void;
}
function SendEmail(props: Props) {
  const { data } = useAppSelector((state: RootState) => state.sendEmailForm);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (data) return; // prevent reset when re-mounting component
    dispatch(
      initialize({
        selectedTemplateId: "",
        searchText: "",
        testEmail: "",
      }),
    );
  }, []);
  const [_, navigate] = useLocation();
  const [searchText, setSearchText] = useState("");
  const [emailGuests, { isLoading: isSending }] = useEmailGuestsMutation();

  const {
    data: emailTemplatesResponse,
    isFetching: isFetchingGetEmailTemplates,
    isSuccess: isSuccessGetEmailTemplates,
    isError: isErrorFetchingEmailTemplates,
    error: emailTemplatesError,
  } = useGetEmailTemplatesQuery({ ventId: props.ventId });
  const {
    data: ventResponse,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({
    id: props.ventId,
  });

  const handleSendEmail = async () => {
    if (
      emailTemplatesResponse == null ||
      ventResponse == null ||
      data == null
    ) {
      console.error("undefined");
      return;
    }

    if (data.testEmail !== "" && !isValidEmail(data.testEmail)) {
      displayErrorMessage("Test email address is invalid.");
      return;
    }

    try {
      const request: EmailGuestsRequest = {
        emailTemplateId: data.selectedTemplateId,
        guestIds: props.guestIds,
        testEmail: data.testEmail,
        isAutoSend: false, // always false for manual send
      };
      await emailGuests(request).unwrap();
      props.onClose();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (isErrorFetchingEmailTemplates || isErrorFetchingVent) {
    handleFetchingError(emailTemplatesError ?? ventError);
    return null;
  }

  if (!emailTemplatesResponse || !ventResponse) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  let rows: JSX.Element[] = [];
  let isSelectedInFilteredEmailTemplates = false;
  if (
    data &&
    isSuccessGetEmailTemplates &&
    emailTemplatesResponse.emailTemplates &&
    emailTemplatesResponse.emailTemplates.length > 0
  ) {
    const filteredEmailTemplates = emailTemplatesResponse.emailTemplates.filter(
      (emailTemplate) =>
        emailTemplate.name.toLowerCase().includes(searchText.toLowerCase()),
    );
    isSelectedInFilteredEmailTemplates = filteredEmailTemplates.some(
      (emailTemplate) => {
        if (emailTemplate.id === data.selectedTemplateId) {
          return true;
        }
      },
    );
    const sorted = filteredEmailTemplates.sort((a, b) =>
      a.updateTime < b.updateTime ? 1 : -1,
    );
    rows = sorted.map((emailTemplate) => (
      <Table.Tr key={emailTemplate.id}>
        <Table.Td>
          <Checkbox
            aria-label="Select row"
            checked={data.selectedTemplateId == emailTemplate.id}
            onChange={(event) => {
              dispatch(
                updateField({
                  field: "selectedTemplateId",
                  value: event.currentTarget.checked ? emailTemplate.id : "",
                }),
              );
            }}
          />
        </Table.Td>
        <Table.Td>
          <Group>
            <EbActionIcon
              aria-label="Configure email template"
              onClick={() => {
                navigate(
                  `/console/emailTemplate/event/${props.ventId}/emailTemplate/${emailTemplate.id}`,
                );
              }}
            >
              <EbTooltip label="Configure email template">
                <IconEmailTemplate />
              </EbTooltip>
            </EbActionIcon>
          </Group>
        </Table.Td>
        <Table.Td>{emailTemplate.name}</Table.Td>
        <Table.Td>{emailTemplate.subject}</Table.Td>
        <Table.Td>{dayjs(emailTemplate.updateTime).format("LLL z")}</Table.Td>
      </Table.Tr>
    ));
  }
  return (
    <Paper>
      <Group justify="space-between" mb="xs" gap="0">
        <Stack gap="0">
          <Text mt="0" size="xl">
            Send Emails Using Selected Template
          </Text>
          <Text mt="0" size="xl">{`Event: ${ventResponse?.name ?? ""}`}</Text>
        </Stack>
      </Group>
      <LinkAnchorToVentEmailTemplates ventId={props.ventId} size="xs">
        Click to create email templates
      </LinkAnchorToVentEmailTemplates>
      <TextInput
        mt="md"
        mb="md"
        label="Test email (sends only one email and only to this address)"
        placeholder="Optional email address for testing"
        value={data?.testEmail ?? ""}
        onChange={(guest) => {
          dispatch(
            updateField({
              field: "testEmail",
              value: guest.currentTarget.value,
            }),
          );
        }}
      />
      <TextSearchBox setSearchText={setSearchText} searchText={searchText} />
      <Skeleton visible={isFetchingGetEmailTemplates}>
        <div hidden={!isSuccessGetEmailTemplates || rows.length === 0}>
          <Table stickyHeader stickyHeaderOffset={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th></Table.Th>
                <Table.Th>Actions</Table.Th>
                <Table.Th>Template name</Table.Th>
                <Table.Th>Subject</Table.Th>
                <Table.Th>Updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </div>
        <div hidden={isFetchingGetEmailTemplates || rows.length > 0}>
          No email templates displayed. Change search or add an email template.
          You need an email template in order to send emails.
        </div>
      </Skeleton>
      <Flex justify="flex-end">
        <Button mt="md" mr="md" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          mt="md"
          onClick={handleSendEmail}
          disabled={
            data?.selectedTemplateId === "" ||
            isSending ||
            !isSelectedInFilteredEmailTemplates
          }
          rightSection={isSending ? <Loader size={14} /> : null}
        >
          {data?.testEmail == ""
            ? `Send Email to ${props.guestIds.length} Guest(s)`
            : "Send Test Email"}
        </Button>
      </Flex>
    </Paper>
  );
}

export default SendEmail;
